import { useState, useEffect, useRef, useCallback } from 'react';
import { URLStateManager, URLState, URLUpdateOptions } from '../services';

interface UseURLStateConfig {
  autoSync?: boolean; // Automatically sync state changes with URL
  syncOnMount?: boolean; // Sync initial state from URL on mount
}

interface UseURLStateReturn {
  // Current state
  urlState: URLState;

  // Page management
  page: number;
  setPage: (page: number, options?: URLUpdateOptions) => void;

  // Search management
  searchQuery: string;
  setSearchQuery: (query: string, options?: URLUpdateOptions) => void;
  clearSearch: (options?: URLUpdateOptions) => void;

  // Filter management
  activeFilters: number[];
  setActiveFilters: (filters: number[], options?: URLUpdateOptions) => void;
  addFilter: (filterId: number, options?: URLUpdateOptions) => void;
  removeFilter: (filterId: number, options?: URLUpdateOptions) => void;
  toggleFilter: (filterId: number, options?: URLUpdateOptions) => void;
  clearFilters: (options?: URLUpdateOptions) => void;
  isFilterActive: (filterId: number) => boolean;

  // General parameter management
  getParameter: (key: string) => string | null;
  setParameter: (key: string, value: string, options?: URLUpdateOptions) => void;
  removeParameter: (key: string, options?: URLUpdateOptions) => void;

  // Utility functions
  reset: () => void;
  getAPIParams: () => { page: number; search?: string; tags?: string };
  buildURL: (state: URLState, baseUrl?: string) => string;
  scrollToTop: (elementId?: string, behavior?: ScrollBehavior) => void;

  // State management
  updateState: (newState: URLState, options?: URLUpdateOptions) => void;
}

export const useURLState = (config: UseURLStateConfig = {}): UseURLStateReturn => {
  const { autoSync = true, syncOnMount = true } = config;

  // Create URL manager instance
  const managerRef = useRef<URLStateManager>();
  const [urlState, setUrlState] = useState<URLState>({});

  // Initialize manager
  useEffect(() => {
    managerRef.current = new URLStateManager();

    if (syncOnMount) {
      setUrlState(managerRef.current.getCurrentState());
    }
  }, [syncOnMount]);

  // Listen for browser navigation changes
  useEffect(() => {
    if (!managerRef.current || !autoSync) return;

    const cleanup = managerRef.current.onStateChange((newState) => {
      setUrlState(newState);
    });

    return cleanup;
  }, [autoSync]);

  // Update internal state when URL changes
  const syncFromURL = useCallback(() => {
    if (managerRef.current) {
      setUrlState(managerRef.current.getCurrentState());
    }
  }, []);

  // Update URL state and optionally sync internal state
  const updateState = useCallback(
    (newState: URLState, options: URLUpdateOptions = {}) => {
      if (!managerRef.current) return;

      managerRef.current.updateState(newState, options);

      if (autoSync) {
        syncFromURL();
      }
    },
    [autoSync, syncFromURL]
  );

  // Page management
  const setPage = useCallback(
    (page: number, options: URLUpdateOptions = {}) => {
      updateState({ page }, options);
    },
    [updateState]
  );

  // Search management
  const setSearchQuery = useCallback(
    (query: string, options: URLUpdateOptions = { resetPage: true }) => {
      updateState({ search: query }, options);
    },
    [updateState]
  );

  const clearSearch = useCallback(
    (options: URLUpdateOptions = { resetPage: true }) => {
      setSearchQuery('', options);
    },
    [setSearchQuery]
  );

  // Filter management
  const setActiveFilters = useCallback(
    (filters: number[], options: URLUpdateOptions = { resetPage: true }) => {
      updateState({ filters }, options);
    },
    [updateState]
  );

  const addFilter = useCallback(
    (filterId: number, options: URLUpdateOptions = { resetPage: true }) => {
      if (managerRef.current) {
        managerRef.current.addFilter(filterId, options);
        if (autoSync) syncFromURL();
      }
    },
    [autoSync, syncFromURL]
  );

  const removeFilter = useCallback(
    (filterId: number, options: URLUpdateOptions = { resetPage: true }) => {
      if (managerRef.current) {
        managerRef.current.removeFilter(filterId, options);
        if (autoSync) syncFromURL();
      }
    },
    [autoSync, syncFromURL]
  );

  const toggleFilter = useCallback(
    (filterId: number, options: URLUpdateOptions = { resetPage: true }) => {
      if (managerRef.current) {
        managerRef.current.toggleFilter(filterId, options);
        if (autoSync) syncFromURL();
      }
    },
    [autoSync, syncFromURL]
  );

  const clearFilters = useCallback(
    (options: URLUpdateOptions = { resetPage: true }) => {
      setActiveFilters([], options);
    },
    [setActiveFilters]
  );

  const isFilterActive = useCallback((filterId: number): boolean => {
    return managerRef.current ? managerRef.current.isFilterActive(filterId) : false;
  }, []);

  // General parameter management
  const getParameter = useCallback((key: string): string | null => {
    return managerRef.current ? managerRef.current.getParameter(key) : null;
  }, []);

  const setParameter = useCallback(
    (key: string, value: string, options: URLUpdateOptions = {}) => {
      updateState({ [key]: value }, options);
    },
    [updateState]
  );

  const removeParameter = useCallback(
    (key: string, options: URLUpdateOptions = {}) => {
      updateState({ [key]: undefined }, options);
    },
    [updateState]
  );

  // Utility functions
  const reset = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.reset();
      if (autoSync) syncFromURL();
    }
  }, [autoSync, syncFromURL]);

  const getAPIParams = useCallback(() => {
    return managerRef.current ? managerRef.current.getAPIParams() : { page: 1 };
  }, []);

  const buildURL = useCallback((state: URLState, baseUrl?: string): string => {
    return managerRef.current ? managerRef.current.buildURL(state, baseUrl) : '';
  }, []);

  const scrollToTop = useCallback((elementId?: string, behavior: ScrollBehavior = 'smooth') => {
    if (managerRef.current) {
      managerRef.current.scrollToTop(elementId, behavior);
    }
  }, []);

  // Derived state
  const page = urlState.page ?? 1;
  const searchQuery = urlState.search ?? '';
  const activeFilters = urlState.filters || [];

  return {
    // Current state
    urlState,

    // Page management
    page,
    setPage,

    // Search management
    searchQuery,
    setSearchQuery,
    clearSearch,

    // Filter management
    activeFilters,
    setActiveFilters,
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    isFilterActive,

    // General parameter management
    getParameter,
    setParameter,
    removeParameter,

    // Utility functions
    reset,
    getAPIParams,
    buildURL,
    scrollToTop,

    // State management
    updateState,
  };
};
